/**
 * Interface defining password strength criteria
 */
interface PasswordStrengthResult {
  id: number;
  value: string;
  contains: string[];
  length: number;
}

/**
 * Checks the strength of a given password
 * Returns an object containing the password's strength level and requirements
 * 
 * @param {string} password - The password to check
 * @returns {Object} Password strength analysis result
 */
export function passwordStrengthChecker(password: string): PasswordStrengthResult {
  // Create our own validation result
  const hasUppercase = /[A-Z]/.test(password);
  const hasLowercase = /[a-z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const hasSymbol = /[^A-Za-z0-9]/.test(password);
  const hasMinLength = password.length >= 6;
  
  // Create a custom contains array based on our validation
  const customContains: string[] = [];
  if (hasUppercase) customContains.push("uppercase");
  if (hasLowercase) customContains.push("lowercase");
  if (hasNumber) customContains.push("number");
  if (hasSymbol) customContains.push("symbol");
  
  // Calculate strength based on our requirements
  let strengthId = 0;
  let strengthValue = "Too weak";
  
  if (hasMinLength) {
    const criteriaCount = [hasUppercase, hasNumber, hasSymbol].filter(Boolean).length;
    
    if (criteriaCount >= 3) {
      strengthId = 3;
      strengthValue = "Strong";
    } else if (criteriaCount >= 2) {
      strengthId = 2;
      strengthValue = "Medium";
    } else if (criteriaCount >= 1) {
      strengthId = 1;
      strengthValue = "Weak";
    }
  }
  
  // Create a result object that matches the expected structure
  const result: PasswordStrengthResult = {
    id: strengthId,
    value: strengthValue,
    contains: customContains,
    length: password.length
  };
  
  return result;
}
